import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { isEqual, isFunction, isUndefined } from 'lodash';
import type { IndexedDBStorageReadResult } from 'services/storage/indexedDBStorageManager.types';
import {
  getPreferenceResult,
  isQuotaError,
  migrateLegacyLocalStorageKey,
  setPreference,
  subscribePreferenceChanges,
  subscribePreferenceRecovery,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from '../../utilities';

// Manages UI preference state persisted in IndexedDB with legacy localStorage migration.
export const useLocalStorage = <T = any>(key: string, initialValue?: T) => {
  const [storedValue, setStoredValue] = useState<T>(initialValue as T);
  // IndexedDB reads are async, so callers need an explicit hydration signal
  // before showing persisted UI state such as feature flags or onboarding prompts.
  const [isReady, setIsReady] = useState(false);
  const storedValueRef = useRef(storedValue);
  const activeKeyRef = useRef(key);
  const valueRevisionRef = useRef(0);
  storedValueRef.current = storedValue;
  // Track the latest initialValue so the effect can reset to it without being
  // listed as a dependency (avoids re-running load on every render).
  const initialValueRef = useRef(initialValue);
  initialValueRef.current = initialValue;

  // Hydrate the active preference key while the shared manager owns any recovery retries.
  useEffect(() => {
    let cancelled = false;
    const hydrationRevision = valueRevisionRef.current;
    const keyChanged = activeKeyRef.current !== key;
    activeKeyRef.current = key;

    if (keyChanged) {
      const nextValue = initialValueRef.current as T;
      storedValueRef.current = nextValue;
      setStoredValue(nextValue);
    }
    setIsReady(false);

    const applyStorageResult = (result: IndexedDBStorageReadResult<T>) => {
      if (cancelled || valueRevisionRef.current !== hydrationRevision) {
        return;
      }

      if (result.status === 'unavailable') {
        // Do not block rendering or replace the last confirmed value while recovery runs.
        setIsReady(true);
        return;
      }

      const nextValue = result.status === 'found' ? result.value : (initialValueRef.current as T);
      // Explicitly reset to initialValue when the key has no stored entry so
      // a key change (e.g. valuations_column_visibility_${sectionId}) does
      // not inherit the previous key's state.
      // Hydration can deliver the same value under a new reference and would otherwise re-render the tree.
      if (!isEqual(storedValueRef.current, nextValue)) {
        storedValueRef.current = nextValue;
        setStoredValue(nextValue);
      }
      setIsReady(true);
    };

    const unsubscribeRecovery = subscribePreferenceRecovery<T>(key, applyStorageResult);

    const load = async () => {
      try {
        await migrateLegacyLocalStorageKey(key);
        applyStorageResult(await getPreferenceResult<T>(key));
      } catch (error) {
        Logger.debug(`Error reading preference key "${key}":`, error);
        applyStorageResult({ error, status: 'unavailable' });
      }
    };

    load();

    return () => {
      cancelled = true;
      unsubscribeRecovery();
    };
  }, [key]);

  // Keep the latest confirmed preference synchronized across tabs and same-tab consumers.
  useEffect(
    () =>
      subscribePreferenceChanges(({ key: changedKey, value }) => {
        if (changedKey === key) {
          // Same-tab broadcast is delivered twice, so ignore duplicates that only differ by reference.
          if (isEqual(storedValueRef.current, value)) {
            return;
          }
          // Update the ref synchronously so getCurrentValue() is not stale during the
          // window between the BroadcastChannel message and the React re-render commit.
          valueRevisionRef.current += 1;
          storedValueRef.current = value as T;
          setStoredValue(value as T);
          // A broadcasted value is already authoritative for this key, even if the
          // initial IndexedDB read is still resolving.
          setIsReady(true);
        }
      }),
    [key]
  );

  const setValue = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      setStoredValue(prevStoredValue => {
        const valueToStore = isFunction(value) ? value(prevStoredValue as T) : value;

        if (!isUndefined(valueToStore)) {
          valueRevisionRef.current += 1;
          storedValueRef.current = valueToStore;
          setPreference(key, valueToStore).catch(error => {
            if (isQuotaError(error)) {
              Logger.warn(`Quota exceeded saving preference key "${key}"`);
            } else {
              Logger.warn(`Error saving preference key "${key}":`, error);
            }
            Sentry.captureException(error, {
              extra: { preferenceKey: key },
            });
          });

          setIsReady(true);
          return valueToStore;
        }

        return prevStoredValue;
      });
    },
    [key]
  );

  const getCurrentValue = useCallback((): T => storedValueRef.current as T, []);

  return [storedValue, setValue, getCurrentValue, isReady] as const;
};

export default useLocalStorage;
